import { cn } from "../lib/cn.js";

/**
 * Loading, failed and empty, restyled for slice 5B.
 *
 * The looks changed and the semantics deliberately did not. `role="status"`,
 * `role="alert"` and the literal button text "Try again" are asserted across
 * six test files and the browser suite, and keeping them is most of what let a
 * full restyle happen without rewriting the assertions that describe how the
 * app behaves. The wrapper elements are also kept flat on purpose: one case
 * finds the message by its text and then asserts `role` on that same element,
 * so wrapping the words in a styled span would break it.
 */

interface LoadingProps {
  label: string;
}

/** Announced, not only shown, so a screen reader hears the wait. */
export function Loading({ label }: LoadingProps) {
  return (
    <p className="text-sm text-ink-muted" role="status">
      {label}
    </p>
  );
}

interface FailedProps {
  message: string;
  onRetry: () => void;
}

export function Failed({ message, onRetry }: FailedProps) {
  return (
    <div className="flex flex-col items-start gap-3">
      <p
        className="rounded-2xl border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger-ink"
        role="alert"
      >
        {message}
      </p>
      <button type="button" onClick={onRetry} className={buttonClass}>
        Try again
      </button>
    </div>
  );
}

interface EmptyProps {
  children: React.ReactNode;
}

export function Empty({ children }: EmptyProps) {
  return <p className="text-sm text-ink-muted">{children}</p>;
}

/**
 * The next page of a cursor-paged list. A failure is said in words above the
 * button, because the button looks the same before and after one.
 */
export function LoadOlder({
  loading,
  failed,
  failedMessage,
  onLoad,
}: {
  loading: boolean;
  failed: boolean;
  failedMessage: string;
  onLoad: () => void;
}) {
  return (
    <div className="flex flex-col items-start gap-3">
      {failed && (
        <p
          className="rounded-2xl border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger-ink"
          role="alert"
        >
          {failedMessage}
        </p>
      )}
      <button className={secondaryButtonClass} type="button" disabled={loading} onClick={onLoad}>
        {loading ? "Loading…" : "Load older"}
      </button>
    </div>
  );
}

export const buttonClass = cn(
  "inline-flex items-center justify-center gap-2 rounded-full px-4 py-2",
  "text-sm font-medium transition-colors",
  "bg-btn text-btn-ink hover:opacity-90",
);

export const secondaryButtonClass = cn(
  "inline-flex items-center justify-center gap-2 rounded-full px-4 py-2",
  "text-sm font-medium transition-colors",
  "border border-line text-ink hover:border-line-strong hover:bg-line/40",
);
